import React from "react";
import RiskDetailTabs from "./RiskDetailTabs";
import { formatAge, formatLocal } from "./socFreshness";
import { isUnassigned } from "./socQueue";

// Timeline from what the queue actually knows: creation, assignment (with its time only when the
// Defender read supplied assignedAt) and an existing ticket. Nothing is invented.
export const timelineOf = (row, ownerLabel) => {
  const items = [];
  if (row.created) items.push({ key: "created", when: formatLocal(row.created), text: `Raised by ${row.source}` });
  if (!isUnassigned(row)) {
    items.push({ key: "assigned", when: row.assignedAt ? formatLocal(row.assignedAt) : "Time not recorded", text: `Assigned to ${ownerLabel(row)}` });
  }
  const ticketKey = row.jiraIssueKey || row.serviceNowIncidentNumber;
  if (ticketKey) items.push({ key: "ticket", when: "Time not recorded", text: `Ticket ${ticketKey} created` });
  return items;
};

// One incident's details, opened under its row in the incident queue (/soc) - the Axaris pattern of
// expanding a risk in place, as All Risks does. The queue's facts stay on top; below them the risk's
// tabs are the same ones All Risks shows (RiskDetailTabs), with the queue's own Incident summary first
// and its Timeline last.
// `risk` is the RiskDetailsDto from GET /allrisks/details: undefined while it loads, null when it
// couldn't be read or isn't there. The tabs then work from what the queue row knows.
const SocIncidentDetails = ({ row, risk, ownerLabel }) => {
  const facts = [
    ["Status", row.status],
    ["Owner", ownerLabel(row)],
    ["Source", row.source],
    ["Area", row.areaLabel],
    ["Age", row.ageMs != null ? formatAge(row.ageMs) : "Unknown"],
    ["Raised", row.created ? formatLocal(row.created) : "Unknown"],
  ];
  const tabRisk = risk || { id: row.id, riskDescription: row.description || null };

  const incidentTab = {
    id: "incident",
    label: "Incident",
    icon: "🚨",
    render: () => (
      <div className="space-y-4 text-sm" data-testid="soc-incident-summary">
        <section>
          <h4 className="font-semibold text-gray-700">What happened</h4>
          <p className="mt-1 whitespace-pre-line text-gray-700">{row.description || "No description recorded."}</p>
        </section>
        <section>
          <h4 className="font-semibold text-gray-700">Recommended next step</h4>
          {row.remediationTitle || row.actionDescription ? (
            <>
              {row.remediationTitle && <p className="mt-1 font-medium text-gray-800">{row.remediationTitle}</p>}
              {row.actionDescription && <p className="mt-1 whitespace-pre-line text-gray-600">{row.actionDescription}</p>}
            </>
          ) : (
            <p className="mt-1 text-gray-600">No remediation recorded yet.</p>
          )}
        </section>
      </div>
    ),
  };
  const timelineTab = {
    id: "timeline",
    label: "Timeline",
    icon: "🕒",
    render: () => (
      <ol className="space-y-3 text-sm" data-testid="soc-incident-timeline">
        {timelineOf(row, ownerLabel).map((item) => (
          <li key={item.key} className="border-l-2 border-gray-200 pl-3">
            <p className="text-xs text-gray-500">{item.when}</p>
            <p className="text-gray-800">{item.text}</p>
          </li>
        ))}
      </ol>
    ),
  };

  return (
    <div className="space-y-3" data-testid={`soc-incident-details-${row.id}`}>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3 lg:grid-cols-6" data-testid="soc-incident-facts">
        {facts.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-content-muted">{label}</dt>
            <dd className="truncate text-content-primary" title={String(value)}>{value}</dd>
          </div>
        ))}
      </dl>
      {risk === null && (
        <p className="text-xs text-content-muted" data-testid="soc-incident-partial">
          The full risk details couldn't be loaded, so the tabs show what the queue knows.
        </p>
      )}
      <RiskDetailTabs risk={tabRisk} loading={risk === undefined} leadingTabs={[incidentTab]} extraTabs={[timelineTab]} />
    </div>
  );
};

export default SocIncidentDetails;
